import React, { useState, useEffect } from "react";
import { db, auth } from "./firebase.js";
import { collection, addDoc, updateDoc, deleteDoc, doc, getDocs } from "firebase/firestore";

function OwnerForm({ onSubmit, onCancel, initialData = {}, isSubmitting, isMobile }) {
  const [formData, setFormData] = useState({
    address: initialData.address || "",
    tract: initialData.tract || "",
    name1: initialData.name1 || "",
    name2: initialData.name2 || "",
    mailing_address: initialData.mailing_address || "",
    phone1: initialData.phone1 || "",
    phone2: initialData.phone2 || "",
    email1: initialData.email1 || "",
    email2: initialData.email2 || "",
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="modal-overlay">
      <div className={`modal-content ${isMobile ? 'modal-content-mobile' : ''}`}>
        <h2 className="modal-title">{initialData.address ? "Edit Owner" : "Add Owner"}</h2>
        <form onSubmit={handleSubmit}>
          <div className={`form-grid ${isMobile ? 'form-grid-mobile' : ''}`}>
            <div className="form-group">
              <label className="form-label">Address</label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                className="form-input"
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Tract</label>
              <input
                type="text"
                name="tract"
                value={formData.tract}
                onChange={handleChange}
                className="form-input"
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Name 1</label>
              <input
                type="text"
                name="name1"
                value={formData.name1}
                onChange={handleChange}
                className="form-input"
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Name 2</label>
              <input
                type="text"
                name="name2"
                value={formData.name2}
                onChange={handleChange}
                className="form-input"
              />
            </div>
            <div className="form-group full-width">
              <label className="form-label">Mailing Address</label>
              <textarea
                name="mailing_address"
                value={formData.mailing_address}
                onChange={handleChange}
                className="form-textarea"
                rows="3"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Phone 1</label>
              <input
                type="text"
                name="phone1"
                value={formData.phone1}
                onChange={handleChange}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Phone 2</label>
              <input
                type="text"
                name="phone2"
                value={formData.phone2}
                onChange={handleChange}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Email 1</label>
              <input
                type="email"
                name="email1"
                value={formData.email1}
                onChange={handleChange}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Email 2</label>
              <input
                type="email"
                name="email2"
                value={formData.email2}
                onChange={handleChange}
                className="form-input"
              />
            </div>
          </div>
          <div className="modal-buttons">
            <button
              type="button"
              onClick={onCancel}
              className="cancel-button"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="save-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function OwnersPage({ isAdmin, isMobile }) {
  const [owners, setOwners] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editOwner, setEditOwner] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      console.log("Auth state changed. Current user:", currentUser ? currentUser.uid : "Not logged in");
      setUser(currentUser);
      if (!currentUser) {
        setError("Please log in to view owners.");
        setIsLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchOwners = async () => {
      if (!user) {
        console.log("Skipping fetch: User not authenticated yet.");
        return;
      }

      setIsLoading(true);
      try {
        console.log("Fetching owners for user:", user.uid);
        const token = await user.getIdToken();
        console.log("Auth token:", token);
        console.log("Database ID:", db.app.options.projectId);
        const ownersCollection = collection(db, "owners");
        console.log("Collection path:", ownersCollection.path);
        const snapshot = await getDocs(ownersCollection);
        console.log("Snapshot size:", snapshot.size);
        if (isMounted) {
          const ownersList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          console.log("Owners list:", ownersList);
          setOwners(ownersList);
          setIsLoading(false);
        }
      } catch (err) {
        console.error("Error fetching owners:", err.message);
        if (isMounted) {
          setError("Failed to load owners: " + err.message);
          setIsLoading(false);
        }
      }
    };

    fetchOwners();

    return () => {
      isMounted = false;
    };
  }, [user]);

  console.log("OwnersPage rendering with isAdmin:", isAdmin); // Debug log to verify isAdmin

  const handleAddOwner = async (newOwner) => {
    if (!isAdmin) {
      setError("Only admins can add owners.");
      return;
    }
    try {
      setError(null);
      setIsSubmitting(true);
      const ownersCollection = collection(db, "owners");
      await addDoc(ownersCollection, newOwner);
      setShowAddForm(false);
      const snapshot = await getDocs(ownersCollection);
      const ownersList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setOwners(ownersList);
    } catch (err) {
      if (err.code === "permission-denied") {
        setError("Only admins can add owners.");
      } else {
        setError("Failed to add owner: " + err.message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditOwner = async (updatedOwner) => {
    if (!isAdmin) {
      setError("Only admins can edit owners.");
      return;
    }
    try {
      setError(null);
      setIsSubmitting(true);
      const ownerDoc = doc(db, "owners", editOwner.id);
      await updateDoc(ownerDoc, updatedOwner);
      setEditOwner(null);
      const ownersCollection = collection(db, "owners");
      const snapshot = await getDocs(ownersCollection);
      const ownersList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setOwners(ownersList);
    } catch (err) {
      if (err.code === "permission-denied") {
        setError("Only admins can edit owners.");
      } else {
        setError("Failed to edit owner: " + err.message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteOwner = async (ownerId) => {
    if (!isAdmin) {
      setError("Only admins can delete owners.");
      return;
    }
    if (window.confirm("Are you sure you want to delete this owner?")) {
      try {
        setError(null);
        setIsSubmitting(true);
        const ownerDoc = doc(db, "owners", ownerId);
        await deleteDoc(ownerDoc);
        const ownersCollection = collection(db, "owners");
        const snapshot = await getDocs(ownersCollection);
        const ownersList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setOwners(ownersList);
      } catch (err) {
        if (err.code === "permission-denied") {
          setError("Only admins can delete owners.");
        } else {
          setError("Failed to delete owner: " + err.message);
        }
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <div className="page-container">
      <style>
        {`
          .page-container {
            padding: 2rem;
          }
          .page-title {
            font-size: 1.5rem;
            margin-bottom: 1rem;
          }
          .error-message {
            background: rgba(255, 0, 0, 0.1);
            padding: 1rem;
            margin-bottom: 1rem;
            border-radius: 0.5rem;
            color: #991b1b;
          }
          .add-button {
            background: #1a3c34;
            color: white;
            padding: 0.5rem 1rem;
            border-radius: 0.25rem;
          }
          .add-button:hover {
            background: #14532d;
          }
          .add-button:disabled {
            background: #6b7280;
          }
          .table-container {
            overflow-x: auto;
            position: relative;
            z-index: 1;
          }
          .owner-table {
            min-width: 100%;
            border-collapse: collapse;
          }
          .table-header {
            background: #1a3c34;
            color: white;
          }
          .table-header-cell {
            border: 1px solid #d1d5db;
            padding: 0.5rem;
            text-align: left;
            white-space: nowrap;
          }
          .table-row:nth-child(even) {
            background: #f3f4f6;
          }
          .table-row:nth-child(odd) {
            background: #e5e7eb;
          }
          .table-cell {
            border: 1px solid #d1d5db;
            padding: 0.5rem;
            white-space: pre-line;
          }
          .action-buttons {
            display: flex;
            gap: 0.5rem;
          }
          .edit-button {
            background: #3b82f6;
            color: white;
            padding: 0.25rem 0.5rem;
            border-radius: 0.25rem;
          }
          .edit-button:hover {
            background: #2563eb;
          }
          .edit-button:disabled {
            background: #93c5fd;
          }
          .delete-button {
            background: #ef4444;
            color: white;
            padding: 0.25rem 0.5rem;
            border-radius: 0.25rem;
          }
          .delete-button:hover {
            background: #dc2626;
          }
          .delete-button:disabled {
            background: #f87171;
          }
          .loading-message,
          .no-owners {
            border: 1px solid #d1d5db;
            padding: 0.5rem;
            text-align: center;
          }
          .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 1000;
          }
          .modal-content {
            background: white;
            padding: 1.5rem;
            border-radius: 0.5rem;
            box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
            width: 90%;
            max-width: 800px;
            z-index: 1001;
          }
          .modal-title {
            font-size: 1.25rem;
            margin-bottom: 1rem;
          }
          .form-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 1rem;
          }
          .form-group {
            display: flex;
            flex-direction: column;
            gap: 0.25rem;
          }
          .form-label {
            font-size: 0.875rem;
            font-weight: 500;
          }
          .form-input {
            border: 1px solid #d1d5db;
            padding: 0.5rem;
            border-radius: 0.25rem;
            width: 100%;
            font-size: 1rem;
          }
          .form-textarea {
            border: 1px solid #d1d5db;
            padding: 0.5rem;
            border-radius: 0.25rem;
            width: 100%;
            font-size: 1rem;
            resize: vertical;
          }
          .full-width {
            grid-column: span 2;
          }
          .modal-buttons {
            display: flex;
            justify-content: flex-end;
            gap: 0.5rem;
            margin-top: 1rem;
          }
          .cancel-button {
            background: #6b7280;
            color: white;
            padding: 0.5rem 1rem;
            border-radius: 0.25rem;
          }
          .cancel-button:hover {
            background: #4b5563;
          }
          .cancel-button:disabled {
            background: #9ca3af;
          }
          .save-button {
            background: #3b82f6;
            color: white;
            padding: 0.5rem 1rem;
            border-radius: 0.25rem;
          }
          .save-button:hover {
            background: #2563eb;
          }
          .save-button:disabled {
            background: #93c5fd;
          }
          @media (max-width: 640px) {
            .page-container {
              padding: 1rem;
            }
            .page-title {
              font-size: 1.25rem;
            }
            .error-message {
              font-size: 0.875rem;
              padding: 0.75rem;
            }
            .add-button {
              padding: 0.4rem 0.8rem;
              font-size: 0.9rem;
            }
            .table-header-cell,
            .table-cell {
              padding: 0.4rem;
              font-size: 0.875rem;
            }
            .edit-button,
            .delete-button {
              padding: 0.25rem 0.5rem;
              font-size: 0.875rem;
            }
            .loading-message,
            .no-owners {
              font-size: 0.875rem;
              padding: 0.4rem;
            }
            .modal-content-mobile {
              padding: 1rem;
              max-width: 350px;
            }
            .modal-title {
              font-size: 1rem;
            }
            .form-grid-mobile {
              grid-template-columns: 1fr;
            }
            .form-label {
              font-size: 0.75rem;
            }
            .form-input,
            .form-textarea {
              font-size: 0.9rem;
              padding: 0.4rem;
            }
            .cancel-button,
            .save-button {
              padding: 0.4rem 0.8rem;
              font-size: 0.9rem;
            }
          }
        `}
      </style>
      <h1 className="page-title">Owner Contacts</h1>
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}
      {isAdmin && (
        <button
          onClick={() => setShowAddForm(true)}
          className="add-button"
          disabled={isSubmitting}
        >
          Add New Owner
        </button>
      )}
      {showAddForm && (
        <OwnerForm
          onSubmit={handleAddOwner}
          onCancel={() => setShowAddForm(false)}
          isSubmitting={isSubmitting}
          isMobile={isMobile}
        />
      )}
      {editOwner && (
        <OwnerForm
          onSubmit={handleEditOwner}
          onCancel={() => setEditOwner(null)}
          initialData={editOwner}
          isSubmitting={isSubmitting}
          isMobile={isMobile}
        />
      )}
      <div className="table-container">
        <table className="owner-table">
          <thead className="table-header">
            <tr>
              <th className="table-header-cell">Address</th>
              <th className="table-header-cell">Tract</th>
              <th className="table-header-cell">Name 1</th>
              <th className="table-header-cell">Name 2</th>
              <th className="table-header-cell">Mailing Address</th>
              <th className="table-header-cell">Phone 1</th>
              <th className="table-header-cell">Phone 2</th>
              <th className="table-header-cell">Email 1</th>
              <th className="table-header-cell">Email 2</th>
              {isAdmin && <th className="table-header-cell">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={isAdmin ? "10" : "9"} className="loading-message">
                  Loading owners...
                </td>
              </tr>
            ) : owners.length === 0 ? (
              <tr>
                <td colSpan={isAdmin ? "10" : "9"} className="no-owners">
                  {error ? "Failed to load owners." : "No owners found."}
                </td>
              </tr>
            ) : (
              owners.map((owner, index) => (
                <tr key={owner.id} className="table-row">
                  <td className="table-cell">{owner.address}</td>
                  <td className="table-cell">{owner.tract}</td>
                  <td className="table-cell">{owner.name1}</td>
                  <td className="table-cell">{owner.name2}</td>
                  <td className="table-cell">{owner.mailing_address}</td>
                  <td className="table-cell">{owner.phone1}</td>
                  <td className="table-cell">{owner.phone2}</td>
                  <td className="table-cell">{owner.email1}</td>
                  <td className="table-cell">{owner.email2}</td>
                  {isAdmin && (
                    <td className="table-cell">
                      <div className="action-buttons">
                        <button
                          onClick={() => setEditOwner(owner)}
                          className="edit-button"
                          disabled={isSubmitting}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteOwner(owner.id)}
                          className="delete-button"
                          disabled={isSubmitting}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default OwnersPage;